import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableCell,
  TableHead,
  TableBody,
  TableRow,
  Button,
  Alert,
  FormControl,
  TextField,
} from "@mui/material";
import { useMemo, useRef, useState } from "react";
import { ClipboardDocumentProperty, compareCustomProperty } from "./ImportUtils";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { selectDocumentProperties, upsertDocumentProperty } from "redux/slices/lensEditor";
import { useClipboard } from "helpers/hooks/useClipboard";
import { useSnackbar } from "notistack";
import { EditableClipboardDocumentProperty } from "./EditableDocumentProperty";

export function ImportDocumentPropertiesDialog({ onClose, lensId }: { onClose: () => void; lensId: number }) {
  const [contents, setContents] = useState("");
  const clipboardDocumentProperties = useMemo(() => (contents ? JSON.parse(contents) : []), [contents]);
  const existingDocumentProperties = useAppSelector(selectDocumentProperties);
  const documentPropertyRefs = useRef<(ClipboardDocumentProperty | null)[]>([]);
  const [conflictingKeys, setConflictingKeys] = useState<string[]>([]);
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  useClipboard(setContents);

  const onImport = async () => {
    const keys = existingDocumentProperties
      .filter((cp) => !!documentPropertyRefs.current.find((cr) => compareCustomProperty(cp, cr)))
      .map((cp) => cp.key);

    setConflictingKeys(keys);

    if (keys.length === 0) {
      try {
        const promises = documentPropertyRefs.current.map((documentProperty) => {
          if (documentProperty !== null) {
            return dispatch(
              upsertDocumentProperty({
                key: documentProperty.key,
                displayName: documentProperty.displayName,
                lensId,
                dataType: documentProperty.dataType,
                selectOptions: documentProperty.selectOptions,
                isHidden: documentProperty.isHidden,
                preventsLensHits: documentProperty.preventsLensHits,
                nameMatchers: documentProperty.nameMatchers,
                collectVisibility: documentProperty.collectVisibility,
              })
            ).unwrap();
          } else {
            return Promise.resolve();
          }
        });
        await Promise.all(promises);
        enqueueSnackbar("Document Properties imported", { variant: "success" });
        onClose();
      } catch (e) {
        console.error(e);
        enqueueSnackbar("Unable to import document properties", { variant: "error" });
      }
    }
  };

  return (
    <Dialog open={true} maxWidth="lg" fullWidth>
      <DialogTitle>Import Document Properties</DialogTitle>
      <DialogContent>
        {conflictingKeys.length > 0 && (
          <Alert severity="error">The following keys already exist: {conflictingKeys.join(", ")}</Alert>
        )}
        {!contents && (
          <FormControl fullWidth>
            <TextField
              value={contents}
              onChange={(evt) => setContents(evt.target.value)}
              label="Paste Document Properties"
            />
          </FormControl>
        )}
        {clipboardDocumentProperties.length > 0 && (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Display Name</TableCell>
                <TableCell>Key</TableCell>
                <TableCell>Select Options</TableCell>
                <TableCell>Data Type</TableCell>
                <TableCell>Collect Visibility</TableCell>
                <TableCell>Is Hidden</TableCell>
                <TableCell>Prevents Lens Hits</TableCell>
                <TableCell>Has NameMatchers</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clipboardDocumentProperties.map((documentProperty, index) => (
                <EditableClipboardDocumentProperty
                  key={documentProperty.key}
                  ref={(element) => (documentPropertyRefs.current[index] = element)}
                  documentProperty={documentProperty}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button sx={{ mr: 2 }} variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onImport}>
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
}
